import React, { useState } from "react";
import { api } from "../lib/api";
import { Gavel, Loader2 } from "lucide-react";

export default function LegalArgumentGenerator() {
    const [issue, setIssue] = useState("");
    const [position, setPosition] = useState("");
    const [jurisdiction, setJurisdiction] = useState("");
    const [context, setContext] = useState("");
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e?.preventDefault();
        if (issue.trim().length < 10) {
            setError("Describe the legal issue in at least 10 characters.");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/arguments/generate", {
                issue,
                position: position || undefined,
                jurisdiction: jurisdiction || undefined,
                context: context || undefined,
            });
            const id = data.id;
            for (let i = 0; i < 90; i++) {
                await new Promise((r) => setTimeout(r, 2500));
                const { data: doc } = await api.get(`/arguments/${id}`);
                if (doc.status === "ready") {
                    setReport(doc.report);
                    setLoading(false);
                    return;
                }
                if (doc.status === "failed") {
                    setError(doc.error || "Generation failed.");
                    setLoading(false);
                    return;
                }
            }
            setError("Timed out waiting for the argument. Try again. It may still complete in the background.");
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Generation failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-edit py-10 lg:py-14 max-w-4xl" data-testid="argument-generator">
            <div className="label-eyebrow">Module</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">Legal Argument Generator</h1>
            <p className="mt-3 text-[#0D1B3E]/75 max-w-2xl">
                Issue → Applicable Law → Argument → Counter-arguments → Rebuttal →
                Conclusion. Real citations only; 3 credits per draft.
            </p>

            {!report && (
                <form onSubmit={submit} className="mt-8 card-flat p-7 space-y-4" data-testid="argument-form">
                    <Field
                        label="Legal issue"
                        value={issue}
                        onChange={setIssue}
                        placeholder="e.g. Can a controller rely on legitimate interests to train a generative AI model on publicly accessible user data?"
                        testId="argument-input-issue"
                        multiline rows={3}
                    />
                    <Field label="Position to argue (optional)" value={position} onChange={setPosition}
                           placeholder="e.g. Yes, subject to a documented balancing test." testId="argument-input-position" />
                    <Field label="Jurisdiction focus (optional)" value={jurisdiction} onChange={setJurisdiction}
                           placeholder="e.g. EU + Germany" testId="argument-input-jurisdiction" />
                    <Field label="Background / facts (optional)" value={context} onChange={setContext}
                           placeholder="Brief facts that should inform the analysis." testId="argument-input-context" multiline rows={4} />

                    {error && <p className="text-sm text-red-700" data-testid="argument-error">{error}</p>}
                    <div className="flex justify-end">
                        <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="argument-submit">
                            {loading ? <Loader2 size={14} className="animate-spin" /> : <Gavel size={14} />}
                            Generate argument
                        </button>
                    </div>
                </form>
            )}

            {report && (
                <article className="mt-8 space-y-7" data-testid="argument-report">
                    <Section title="Issue"><p>{report.issue}</p></Section>
                    <Section title="Applicable law">
                        <ul className="space-y-3" data-testid="argument-applicable-law">
                            {(report.applicable_law || []).map((a, i) => (
                                <li key={i} className="border-l-2 border-[#C9A84C] pl-3">
                                    <div className="font-semibold">{a.source}</div>
                                    <p className="text-sm text-[#0D1B3E]/85">{a.explanation}</p>
                                </li>
                            ))}
                        </ul>
                    </Section>
                    <Section title="Argument">
                        <p className="font-display text-xl leading-snug mb-3" data-testid="argument-headline">
                            {report.argument?.headline}
                        </p>
                        <ol className="space-y-3" data-testid="argument-steps">
                            {(report.argument?.steps || []).map((s) => (
                                <li key={s.step} className="flex gap-3">
                                    <span className="font-display text-[#C9A84C] w-6 shrink-0">{s.step}.</span>
                                    <div>
                                        <p className="font-semibold">{s.claim}</p>
                                        <p className="text-sm text-[#0D1B3E]/80 mt-1">{s.support}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </Section>
                    <Section title="Counter-arguments">
                        <ol className="space-y-3" data-testid="argument-counters">
                            {(report.counter_arguments || []).map((c, i) => (
                                <li key={i} className="card-flat p-4">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#0D1B3E]/55">Counter {i + 1}</div>
                                    <p className="font-semibold mt-1">{c.claim}</p>
                                    <p className="text-sm text-[#0D1B3E]/80 mt-1">{c.rationale}</p>
                                    {c.authority && (
                                        <p className="text-[11px] text-[#0D1B3E]/60 mt-1">Authority: {c.authority}</p>
                                    )}
                                </li>
                            ))}
                        </ol>
                    </Section>
                    <Section title="Rebuttal">
                        <ul className="space-y-2" data-testid="argument-rebuttal">
                            {(report.rebuttal || []).map((r, i) => (
                                <li key={i} className="text-sm">
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] mr-2">to #{r.to_counter}</span>
                                    {r.response}
                                </li>
                            ))}
                        </ul>
                    </Section>
                    <Section title="Conclusion">
                        <p className="text-base leading-relaxed" data-testid="argument-conclusion">{report.conclusion}</p>
                    </Section>
                    {report.authorities?.length > 0 && (
                        <Section title="Authorities">
                            <ul className="flex flex-wrap gap-2" data-testid="argument-authorities">
                                {report.authorities.map((a, i) => (
                                    <li key={i} className="text-xs px-2 py-1 border border-[#0D1B3E]/20">
                                        <span className="text-[#C9A84C] uppercase tracking-[0.16em] mr-1">{a.kind}</span>
                                        {a.label}
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    )}
                    {report.caveats?.length > 0 && (
                        <Section title="Caveats">
                            <ul className="list-disc pl-5 text-sm text-[#0D1B3E]/70">
                                {report.caveats.map((c, i) => <li key={i}>{c}</li>)}
                            </ul>
                        </Section>
                    )}
                    <div className="flex flex-wrap gap-3">
                        <button type="button" onClick={() => setReport(null)} className="btn-secondary" data-testid="argument-restart">
                            New argument
                        </button>
                    </div>
                </article>
            )}
        </div>
    );
}

function Field({ label, value, onChange, testId, placeholder, multiline, rows = 2 }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">{label}</span>
            {multiline ? (
                <textarea rows={rows} className="field" value={value} placeholder={placeholder}
                          onChange={(e) => onChange(e.target.value)} data-testid={testId} />
            ) : (
                <input type="text" className="field" value={value} placeholder={placeholder}
                       onChange={(e) => onChange(e.target.value)} data-testid={testId} />
            )}
        </label>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <div className="label-eyebrow">{title}</div>
            <div className="mt-3">{children}</div>
        </section>
    );
}
